import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Button, InlineLoader, Popover, SubTable, Text, useTable } from "@netapp/bxp-design-system-react";
import { apiClient } from "utils/clients";
import externals from "utils/externals";
import { ErrorIcon, NoticeTriangleIcon } from "@netapp/bxp-style/react-icons/Notification";
import styles from "./OrgAdminDetails.module.scss";
import ClaimOrgAdminDialog from "./ClaimOrgAdminDialog";
import { claimOrgAdmin as i18nNamespace } from "../../../../i18n/namespace";
import { useTranslation } from "react-i18next";
import { getSafeModeHeaders } from "../../../../utils/app/utils";

const OrgAdminListTable = React.memo(({orgAdmins}) => {
    const tableColumns = [{
        accessor: 'name',
        id: 'name',
        width: 250
    }];
    const {organizedRows, columns} = useTable({columns: tableColumns, rows: _.drop(orgAdmins)});
    return <>
        <SubTable headerContainerClassName={styles['header-row-hidden']} columns={columns} rows={organizedRows}/>
    </>
})

const OrgAdminDetails = React.memo(() => {
    const dispatch = useDispatch();
    const { selectedOrgId } = useSelector(state => state.tenancy);
    const featuresByKey = useSelector(state => state.features.byKey);
    const [isFetchOrgAdminsInprogress, setIsFetchOrgAdminsInprogress] = useState(true);
    const [hasOrgAdmin, setHasOrgAdmin] = useState(false);
    const [hasMultipleOrgAdmins, setHasMultipleOrgAdmins] = useState(false);
    const [orgAdminsList, setOrgAdminsList] = useState([]);
    const [orgAdminsListWithNameAndEmail, setOrgAdminsListWithNameAndEmail] = useState([]);
    const [hasAPIFailed, setHasAPIFailed] = useState(false);
    const [onContactAdminBtnClick, setOnContactAdminBtnClick] = useState(false);
    const [launchClaimDialog, setLaunchClaimDialog] = useState(false);
    const [isRefreshRequired, setIsRefreshRequired] = useState(true);
    const { t } = useTranslation(i18nNamespace);

    const onAPIFailure = (err) => {
        setIsFetchOrgAdminsInprogress(false);
        setHasAPIFailed(true);
        dispatch({
            type: 'NOTIFICATIONS:ERROR',
            payload: {
                message: 'Failed to fetch organization admin details.',
                exception: err
            }
        });
    }

    useEffect(() => {
        const openEmailClient = () => {
            try {
                const recipient = _.map(orgAdminsListWithNameAndEmail, 'email').join(',');
                window.location.href = `mailto:${recipient}`;
            } catch (err) {
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: 'Failed to launch email client.',
                        exception: err
                    }
                });
            } finally {
                setOnContactAdminBtnClick(false);
            }
        };
        if (onContactAdminBtnClick) openEmailClient();
    }, [onContactAdminBtnClick])

    useEffect(() => {
        const fetchOrgAdminsNameAndEmail = async () => {
            try {
                const filterQuery = _.map(orgAdminsList, (user) => `id eq '${user.id}'`).join(' or ');
                const { data: { items }} = await apiClient.get(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}/users?filter=${filterQuery}`, {
                    headers: {
                        'Accept': 'application/vnd.netapp.bxp.users.extended+json'
                    }});
                setOrgAdminsListWithNameAndEmail(items);
                setIsFetchOrgAdminsInprogress(false);
            } catch (err) {
                onAPIFailure(err)
            }
        }
        if (hasOrgAdmin) fetchOrgAdminsNameAndEmail();
    }, [hasOrgAdmin, orgAdminsList])

    useEffect(() => {
        const fetchOrgAdmins = async () => {
            try {
                setIsFetchOrgAdminsInprogress(true)
                const safeModeHeaders = getSafeModeHeaders(featuresByKey);
                const {data: {count, items}} = await apiClient.get(`${externals.tenancyV4Backend}/resources/${selectedOrgId}/permissions/bxp:organization-delete/users?filter=userType eq 'user'&limit=1000`, safeModeHeaders);
                setOrgAdminsList(items);
                setHasMultipleOrgAdmins(count > 1);
                setHasOrgAdmin(count > 0);
                if (!count) {
                    setIsFetchOrgAdminsInprogress(false);
                }
            } catch (err) {
                onAPIFailure(err);
            } finally {
                setIsRefreshRequired(false);
            }
        }
        if(isRefreshRequired) fetchOrgAdmins();
    }, [isRefreshRequired])

    return <>
        { isFetchOrgAdminsInprogress ? <InlineLoader /> : <>
                { hasAPIFailed && <div className={styles['org-admin-content-value']}>
                        <ErrorIcon color='error' size='16px' />
                        <Text style={{ paddingLeft: '8px' }}>{t('unable_to_retrieve')}</Text>
                    </div>}
                { !hasOrgAdmin && !hasAPIFailed && <div className={styles['org-admin-content-value']}>
                        <NoticeTriangleIcon color='error' size='16px' />
                        <Text style={{ paddingLeft: '8px' }}>{t('unclaimed')}</Text>
                    </div>}
                { hasOrgAdmin && !hasAPIFailed && <div>
                        <Text style={{display: 'inline-flex'}}>{orgAdminsListWithNameAndEmail[0]?.name}</Text>
                        { hasMultipleOrgAdmins && <Popover placement='right' verticalPlacement='top' contentClassName={styles['popover-content-style']} Trigger={() => {
                            return <Button variant="text" style={{paddingLeft: '5px'}}>{`+${orgAdminsListWithNameAndEmail?.length - 1}`}</Button>
                        }}>
                            <OrgAdminListTable orgAdmins={orgAdminsListWithNameAndEmail}></OrgAdminListTable>
                        </Popover>}
                    </div>
                }
                <div className={styles['contact-admin-action-btn']}>
                    {hasOrgAdmin && !hasAPIFailed && <Button variant="text"
                            onClick={() => setOnContactAdminBtnClick(true)}>
                        {t('contact_admins')}
                    </Button>}
                    {!hasOrgAdmin && !hasAPIFailed && <Button variant="text"
                            onClick={() => setLaunchClaimDialog(true)}>
                        {t('claim_role')}
                    </Button>}
                </div>
            </>
        }
        {launchClaimDialog && <ClaimOrgAdminDialog closeTrigger={(isRefreshRequired) => {
                if (isRefreshRequired) setIsRefreshRequired(true);
                setLaunchClaimDialog(false);
            }
        } />}
    </>
});

export default OrgAdminDetails;